// HTML body for "Your Coding Agent Is Silently Doing Nothing"
// Single source of truth used by:
//   - BlogPost.js (dangerouslySetInnerHTML)
//   - scripts/prerender.mjs (noscript injection for SEO)
//
// Keep this in sync with sitemap.xml lastmod when content changes.

const html = `
<p>I spent the better part of an afternoon convinced I had a bad model.</p>

<p>Goose wasn't applying edits. No error. No timeout. No complaint. It would read the file, think
about it, produce what looked like a plan — and then nothing. The file sat unchanged. I swapped
prompts. I tried a different task. I restarted Ollama. Same result. The model —
<strong>qwen2.5-coder:32b</strong>, 32 billion parameters of carefully coder-tuned weights — was
apparently just vibing.</p>

<p>It wasn't the model.</p>

<h2>The thing that's actually happening</h2>

<p>When an agentic coding tool calls a function — "edit this file," "read this path," "apply this
patch" — it sends a request to the model that includes a structured <code>tools</code> block. The
model is supposed to respond with a structured <code>tool_calls</code> array: here is the tool I
want to invoke, here are the arguments.</p>

<p>On raw Ollama endpoints, most qwen-family models don't do that.</p>

<p>They put the tool call in <code>content</code> — the regular text field — and return an empty
<code>tool_calls</code> array. The format varies by model. qwen2.5-coder emits JSON:</p>

<pre><code>content: '{"name": "edit_file", "arguments": {"path": "app.py", "content": "..."}}'
tool_calls: []</code></pre>

<p>qwen3-coder does something different — XML:</p>

<pre><code>content: '&lt;function=edit_file&gt;&lt;parameter=path&gt;app.py&lt;/parameter&gt;...&lt;/function&gt;'
tool_calls: []</code></pre>

<p>The agentic tool checks <code>tool_calls</code>. Finds nothing. Reports no edits. Exits
cleanly.</p>

<p>That's not a failure. That's a success with an empty result. And there is no log line that tells
you why.</p>

<h2>How we found it</h2>

<p>We built a systematic bake-off at QCoda — part of our labs infrastructure for testing and
graduating capabilities before we ship them. The goal was to understand which agentic coding engines
work with which local models, so we could route automatically rather than guess. We ran aider,
goose, pi, claude-code, and opencode against seven local models, ten trials each, same task, same
context.</p>

<p>The task: add four specific routes to an existing Flask application. Specific, measurable,
unambiguous. Either the routes were in the file or they weren't.</p>

<p>Some of the results looked like hardware problems. Goose went 0/10 on qwen2.5-coder:32b.
Claude-code did the same. These aren't obscure tools — they're well-maintained, widely used. And
qwen2.5-coder:32b is not a bad model. It was <em>the fastest model in the entire matrix</em> when
paired with a compatible engine — finishing in 21 seconds median, nothing else close.</p>

<p>Zero edits applied on one engine. Twenty-one seconds to completion on another. Same model. Same
hardware. Same task.</p>

<p>The only difference was whether the engine depended on structured <code>tool_calls</code> or
not.</p>

<p>Aider doesn't use the tool-call protocol. It asks the model to emit edits in a SEARCH/REPLACE
format, reads the response as text, and parses it directly. No <code>tool_calls</code> dependency.
Zero compatibility surface for this failure mode. That's why aider's results were clean across every
model — not because aider is better engineered in some abstract sense, but because it sidesteps the
broken layer entirely.</p>

<h2>The vLLM variant is worse</h2>

<p>We also probed qwen2.5-coder-7b running behind a vLLM server. Raw Ollama is at least
consistently broken — it always emits text. vLLM without the right startup flag is
non-deterministic.</p>

<p>Three consecutive identical requests, temperature zero:</p>

<ul>
  <li>Response 1: <code>&lt;response&gt;</code>-fenced JSON</li>
  <li>Response 2: <code>&lt;function-call&gt;</code> XML</li>
  <li>Response 3: structured <code>tool_calls</code> ✓</li>
</ul>

<p>Same model. Same prompt. Same temperature. Three different formats.</p>

<p>At temp=0, you expect determinism. You don't get it. The fix is a vLLM startup flag:
<code>--enable-auto-tool-choice --tool-call-parser hermes</code>. It has to be set when the server
starts. If you didn't set it, your model is serving non-deterministic tool-call output and every
agent running against it is seeing a different interface on each request. Nothing in the agent's
logs will tell you this is happening.</p>

<h2>The fix is at the serving layer</h2>

<p>Here's the thing about per-client shims: there are five of them by the time you've installed a
few coding agents. Each one has its own way of handling — or not handling — this failure. Some fail
silently. Some error cryptically. Some, like aider, just happen to use a different protocol that
avoids the problem.</p>

<p>The right fix is one layer down: normalize tool-call output at the serving layer, before any
client sees it. Parse the text-form call out of <code>content</code>, build the structured
<code>tool_calls</code> array, return that. Every client downstream gets working tool calls without
knowing a normalization step happened.</p>

<p>That's what we built into <strong>LLMesh</strong> — QCoda's inference node layer. When you point
your local coding agent at a LLMesh endpoint instead of raw Ollama, the normalization happens
transparently. qwen2.5-coder emits JSON-in-content; LLMesh parses it, returns structured
<code>tool_calls</code>. qwen3-coder emits XML; same treatment. The agent never knows.</p>

<p>The practical consequence: qwen2.5-coder:32b behind LLMesh becomes a viable target for goose,
pi, and claude-code — tools that were 0/10 against the raw endpoint. Same model, same hardware.
Different endpoint URL.</p>

<p>Worth noting: LiteLLM proxy does similar normalization, and if you're already running it, you're
getting a lot of this for free. The point isn't that we invented the solution — it's that <em>the
fix needs to live somewhere in the serving layer</em>, and most teams running raw Ollama or vLLM
don't have anything there. The default is broken. Silently.</p>

<h2>Go check what your agents are pointed at</h2>

<p>If it's a raw Ollama endpoint — and most local setups start there — run one test. Pick a
qwen-family model. Pick a tool-dependent agent. Give it a one-line edit task. Watch whether the
file changes. Then look at the agent's output and see if it mentions tool calls at all.</p>

<p>If the file didn't change and there was no error, you found it.</p>

<p>The fix isn't complicated. But you can't fix what you don't know is broken. And this failure is
specifically designed, by accident, to look like a model problem when it's a protocol problem. Teams
swap models. They conclude local OSS isn't ready. They move back to cloud.</p>

<p>That's not an accuracy problem. That's a serving-layer misconfiguration masquerading as a
capability gap.</p>

<hr />

<p><em>This is part of an ongoing series from QCoda Labs — where we run structured experiments to
understand what actually works in local AI-assisted software development, before building on top of
it. Episode 2: which agentic coding engines work for which kind of work, and why the results
surprised us.</em></p>
`;

module.exports = html;
